/**
 * SizilPro — main.tsx
 * App entry point. Bootstraps React, TanStack Query, Router, and auth.
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { Toaster } from "sonner";
import { useAuthStore } from "@/store/authStore";
import { routeTree } from "./routeTree.gen";
import "./index.css";

// ─── QueryClient ─────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 10 seconds — data is considered fresh for 10s after fetching.
      // With refetchOnMount: true, any query older than 10s will refetch
      // when its component mounts. This fixes stale navigation state
      // (delete not working, proposals not loading after route change)
      // without flooding Supabase with requests on every render.
      staleTime:            1000 * 10,
      retry:                1,
      refetchOnMount:       true,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (err) => {
        console.error("[mutation error]", err);
      },
    },
  },
});

// ─── Router ──────────────────────────────────────────────────
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// ─── Bootstrap ───────────────────────────────────────────────
async function bootstrap() {
  await useAuthStore.getState().initAuth();

  const root = document.getElementById("root");
  if (!root) throw new Error("Missing #root element");

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#1a1a1a",
              border:     "1px solid rgba(255,255,255,0.1)",
              color:      "#fff",
            },
          }}
        />
        {import.meta.env.DEV && (
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        )}
      </QueryClientProvider>
    </React.StrictMode>
  );
}

bootstrap();